import { useState } from 'react';
import PrimaryButton from '../../../components/atoms/Button';
import Modal from '../../../components/molecules/Modal/Modal';
import { submitSupportIssue } from '../../../services/user-interaction.service';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { capitalizeText } from '../../../shared/utils/general.util';
import toast from 'react-hot-toast';

const banks = [
	'ABC Capital',
	'Actinver',
	'Afirme',
	'American Express',
	'Autofin',
	'Banca Mifel',
	'Banco Ahorra Famsa',
	'Banco Azteca',
	'Banco Bancrea',
	'Banco Base',
	'Banco Credit Suisse',
	'Banco del Bajío',
	'Bancoppel',
	'Banjercito',
	'Bank of America',
	'Bank of Tokio-Mitsubishi',
	'Banorte',
	'Banregio',
	'Bansy',
	'Barclays',
	'BBVA Bancomer',
	'CI Banco',
	'Citybanamex',
	'Compartamos',
	'Consubanco',
	'Deutsche Bank',
	'Dondé Banco',
	'HSBC México',
	'ICBC',
	'Inbursa',
	'Inmobiliario Mexicano',
	'Interbanco',
	'Interacciones',
	'Investa Bank',
	'Invex',
	'JP Morgan',
	'Mizuho',
	'Monex',
	'Multiva',
	'Nu',
	'S3',
	'Sabadel',
	'Santander',
	'Shinhan',
	'Scotiabank',
	'UBS',
	'Ualá',
	'Ve por más',
	'Volkswagen Bank',
	'Otro',
];

export const ModalPositiveBalance = ({
	open,
	onClose,
	fetchSupportIssues,
}: {
	open: boolean;
	onClose: () => void;
	fetchSupportIssues: () => Promise<void>;
}) => {
	const { taxPayer } = useSelector((state: RootState) => state.taxProfile);
	const userProfile = useSelector((state: RootState) => state?.profile?.user);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [typeOfReturn, setTypeOfReturn] = useState<
		'compensation' | 'refund' | null
	>(null);
	const [currentStep, setCurrentStep] = useState<number>(1);
	const [selectedBank, setSelectedBank] = useState<string>('');
	const [otherBank, setOtherBank] = useState<string>('');
	const [clabe, setClabe] = useState<string>('');

	const handleNextStep = () => {
		if (currentStep === 1) {
			setCurrentStep(currentStep + 1);
		} else {
			handleSubmit();
		}
	};

	const handleClose = () => {
		onClose();
		setCurrentStep(1);
		setTypeOfReturn(null);
		setSelectedBank('');
		setOtherBank('');
		setClabe('');
		setTypeOfReturn(null);
	};

	const isButtonDisabled = () => {
		if (!typeOfReturn) return true;

		if (currentStep === 2 && typeOfReturn === 'refund') {
			const isValidBank =
				selectedBank &&
				(selectedBank !== 'Otro' || (otherBank && otherBank.length >= 3));
			const isValidClabe = clabe.length === 18;

			return !isValidBank || !isValidClabe;
		}

		return false;
	};

	const handleSubmit = async () => {
		const annualDeclarationConfig = {
			typeOfReturn,
			selectedBank,
			otherBank,
			clabe,
		};
		const body = {
			user_id: userProfile?.id,
			category_type: 'positive_balance_annual_declaration',
			category_detail: typeOfReturn,
			message: JSON.stringify(annualDeclarationConfig),
			escalation_type: 'config_annual_declaration',
		};
		try {
			if (!userProfile?.id) return;
			setIsLoading(true);
			await submitSupportIssue(body);
			await fetchSupportIssues();
			handleClose();
			toast.success('Solicitud enviada correctamente');
		} catch (e) {
			toast.error('Ocurrió un error al enviar la solicitud');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Modal
			open={open}
			onClose={handleClose}
			type='fullscreen'
			showCloseButton={true}
			sx='overflow-y-auto w-[90%] max-h-[calc(100dvh-32px)] md:max-h-[calc(100dvh-100px)] max-w-[500px] !p-3'
		>
			<div className='flex flex-col gap-4'>
				<p className='font-bold'>Saldo a favor</p>
				<hr />
				{currentStep === 1 && (
					<div className='text-sm flex flex-col gap-2'>
						<p>
							Si llegas a tener saldo a favor en tu declaración anual, ¿Cómo
							deseas que lo gestionemos?
						</p>
						<select
							value={typeOfReturn || ''}
							name='balanceManagement'
							id='balanceManagement'
							className='p-2 border border-h-neutral-90 rounded-lg outline-none'
							onChange={(e) =>
								setTypeOfReturn(e.target.value as 'compensation' | 'refund')
							}
						>
							<option value=''>Selecciona una opción</option>
							<option value='compensation'>
								Compensación (se utilizará en declaraciones futuras)
							</option>
							<option value='refund'>
								Devolución (el SAT te devuelve el dinero a tu cuenta bancaria)
							</option>
						</select>
					</div>
				)}
				{currentStep === 2 && typeOfReturn === 'compensation' && (
					<div>
						<div className='mt-2 bg-gradient-to-tr from-h-primary-90/80 to-h-primary-90/20 rounded-lg border border-h-primary-100 shadow-sm text-h-dark p-4 mb-4'>
							<h3 className='mb-2 font-bold'>¡Elegiste compensación!</h3>
							<div className='flex flex-col gap-2'>
								<ul className='list-disc pl-4 space-y-2 text-sm'>
									<li>
										La compensación te permite utilizar tu saldo a favor para
										futuros pagos de impuestos.
									</li>
									<li>
										Puedes utilizar tu saldo a favor para cubrir tus próximos
										pagos de impuestos, ya sean mensuales o anuales.
									</li>
									<li>
										Solo podrás aplicarlo a impuestos que debas pagar después de
										presentar tu declaración anual, no a deudas anteriores.
									</li>
									<li>
										Tu saldo a favor de ISR únicamente puede usarse para pagar
										el mismo impuesto (ISR).
									</li>
									<li>
										Esta opción no requiere aprobación del SAT. Al seleccionar
										compensación, podrás utilizar tu saldo a favor disponible.
									</li>
								</ul>
							</div>
						</div>
					</div>
				)}
				{currentStep === 2 && typeOfReturn === 'refund' && (
					<div className='text-sm flex flex-col gap-1'>
						<p className='mb-3'>
							Para que el SAT pueda hacer la devolución de tu dinero a tu
							cuenta, se requieren los siguientes datos:
						</p>
						<input
							readOnly
							value={`Beneficiario: ${capitalizeText(taxPayer?.name!)}`}
							className='p-2 border border-h-neutral-90 rounded-lg outline-none cursor-not-allowed mb-2'
						/>
						<select
							name='bankSelection'
							id='bankSelection'
							className='p-2 border border-h-neutral-90 rounded-lg outline-none'
							onChange={(e) => setSelectedBank(e.target.value)}
							value={selectedBank}
						>
							<option value=''>Selecciona tu banco</option>
							{banks.map((bank) => (
								<option
									key={bank}
									value={bank}
								>
									{bank}
								</option>
							))}
						</select>
						{selectedBank === 'Otro' && (
							<input
								type='text'
								placeholder='Ingresa el nombre de tu banco'
								className='p-2 border border-h-neutral-90 rounded-lg outline-none mt-2'
								value={otherBank}
								onChange={(e) => setOtherBank(e.target.value)}
								minLength={3}
								maxLength={50}
							/>
						)}
						<div className='flex flex-col gap-1'>
							<input
								type='text'
								placeholder='CLABE interbancaria (18 dígitos)'
								className='p-2 border border-h-neutral-90 rounded-lg outline-none mt-2'
								value={clabe}
								onChange={(e) => {
									const value = e.target.value.replace(/\D/g, '');
									if (value.length <= 18) {
										setClabe(value);
									}
								}}
								pattern='\d{18}'
								maxLength={18}
							/>
							<p className='text-xs text-h-neutral-60'>
								Este dato está registrado en tu estado de cuenta bancario o
								puedes validarlo en la app de tú banco.
							</p>
						</div>

						<div className='mt-2 bg-gradient-to-tr from-h-primary-90/80 to-h-primary-90/20 rounded-lg border border-h-primary-100 shadow-sm text-h-dark p-4 mb-4'>
							<h3 className='mb-2 font-bold'>Ten en cuenta que:</h3>
							<div className=' flex flex-col gap-2'>
								<ul className='list-disc pl-4 space-y-2'>
									<li>
										La devolución permite recibir tu saldo a favor directamente
										en tu cuenta bancaria.
									</li>
									<li>
										La cuenta clabe interbancaria debe estar a tu nombre,
										verifica que los datos sean correctos.
									</li>
									<li>
										El SAT puede tardar de 1 a 40 días hábiles en revisar tu
										solicitud tras presentar la declaración anual.
									</li>
									<li>
										Mantente atento a tu buzón tributario, ahí el SAT te
										notificará cualquier novedad.
									</li>
									<li>
										La devolución o rechazo del saldo a favor depende
										completamente del SAT.
									</li>
								</ul>
								<p className='mt-2 text-xs opacity-70'>
									Si tienes saldo a favor y el SAT rechaza la solicitud de
									devolución, tendremos que entrar a analizar cada una de tus
									declaraciones mensuales del 2024 para entender en dónde está
									el problema y generar la corrección necesaria. Dichas
									correcciones pueden generarse gratuitamente, si heru presentó
									todas tus declaraciones mensuales de 2024; si no es así, la
									evaluación y corrección tendrá un costo adicional.
								</p>
							</div>
						</div>
					</div>
				)}
				<div className='flex gap-2'>
					{currentStep === 2 && (
						<PrimaryButton
							variant='outline'
							label='Volver'
							onClick={() => setCurrentStep(currentStep - 1)}
						/>
					)}
					<PrimaryButton
						loading={isLoading}
						disabled={isButtonDisabled()}
						label={currentStep === 1 ? 'Siguiente' : 'Enviar'}
						onClick={handleNextStep}
					/>
				</div>
				<div className='bg-neutral-100 border border-neutral-200 rounded-lg p-4 text-sm'>
					<p className='text-neutral-800'>
						Recuerda que tu selección y datos los vamos a usar únicamente si en
						el resultado que obtengas tienes saldo a favor.
						<b> Esto no es una garantía de que vas a obtener saldo a favor.</b>
					</p>
				</div>
			</div>
		</Modal>
	);
};
